import type { ElementType } from "react";

import {
  BookIcon,
  EmaileditorTitleIcon,
  FileIcon,
  ModelIcon,
  StylesIcon,
  TagIcon,
} from "@hightouch/htui";

import { useLifecycleStudioEnabled } from "src/hooks/use-lifecycle-studio-enabled";
import { useIsAdStudioEnabled } from "src/pages/agents/hooks/use-is-ad-studio-enabled";
import { KNOWLEDGE_BASE } from "src/pages/context-hub/components/use-knowledge-section";
import { useDefinitionsTabEnabled } from "src/pages/context-hub/definitions/use-definitions-tab-enabled";

export type KnowledgeNavItem = {
  label: string;
  /** Route segment under `/context-hub/knowledge`. */
  path: string;
  icon: ElementType;
  isHidden?: boolean;
};

export type KnowledgeNavGates = {
  showStyles: boolean;
  /** Definitions replaces Memories in the same slot. */
  showDefinitions: boolean;
};

/** Knowledge's pages, grouped as its own rail shows them. */
export const knowledgeNavSections = ({
  showStyles,
  showDefinitions,
}: KnowledgeNavGates): Array<{ title: string; items: KnowledgeNavItem[] }> => [
  {
    title: "Context",
    items: [
      { label: "Files", path: "files", icon: FileIcon },
      {
        label: "Styles",
        path: "styles",
        icon: StylesIcon,
        isHidden: !showStyles,
      },
      { label: "Skills", path: "skills", icon: ModelIcon },
      showDefinitions
        ? { label: "Definitions", path: "definitions", icon: BookIcon }
        : { label: "Memories", path: "memories", icon: BookIcon },
    ],
  },
  {
    title: "References",
    items: [
      {
        label: "Copy references",
        path: "copy-references",
        icon: EmaileditorTitleIcon,
      },
      { label: "Tags", path: "tags", icon: TagIcon },
    ],
  },
];

/** Which of Knowledge's optional pages this viewer has. */
export const useKnowledgeNavGates = (): KnowledgeNavGates => {
  const isAdStudioEnabled = useIsAdStudioEnabled();
  const isLifecycleStudioEnabled = useLifecycleStudioEnabled();
  const isDefinitionsTabEnabled = useDefinitionsTabEnabled();
  return {
    showStyles: isAdStudioEnabled || isLifecycleStudioEnabled,
    showDefinitions: isDefinitionsTabEnabled,
  };
};

/** Knowledge's pages in one list, as Context's sidebar shows them, with their full paths. */
export const useKnowledgeLinks = () =>
  knowledgeNavSections(useKnowledgeNavGates()).flatMap(({ items }) =>
    items
      .filter((item) => !item.isHidden)
      .map((item) => ({
        href: `${KNOWLEDGE_BASE}/${item.path}`,
        label: item.label,
        icon: item.icon,
      })),
  );
